/**
 * useAuth — composite auth hook (BUILD_SPEC.md §7.5).
 *
 * Pulls together the in-memory auth store, the API wrappers, and the
 * proactive-refresh ticker so components can simply call:
 *   const { user, login, logout } = useAuth();
 *
 * Two side effects mounted by `useAuthBootstrap()`:
 *   1. A 10-minute interval refreshes the access token in the
 *      background. The token TTL is 15 minutes (§7.4), so refreshing
 *      every 10 keeps us comfortably ahead of expiry without churning.
 *   2. A `focus` listener refreshes when the user comes back to the
 *      tab — covers the "left for lunch" case where the timer fired
 *      while the tab was discarded.
 */

import { useCallback, useEffect } from "react";
import { useAuthStore } from "@/stores/authStore";
import { loginRequest, logoutRequest } from "@/api/auth";
import { proactiveRefresh } from "@/api/client";

/** Pure state-shape hook — no side effects. Use this in render code. */
export function useAuth() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);
  const clear = useAuthStore((s) => s.clear);

  const login = useCallback(
    async (username: string, password: string) => {
      const { accessToken: token, user: u } = await loginRequest(username, password);
      setSession({ accessToken: token, user: u });
    },
    [setSession],
  );

  const logout = useCallback(async () => {
    await logoutRequest();
    clear();
  }, [clear]);

  return { accessToken, user, isAuthed: !!accessToken && !!user, login, logout };
}

/**
 * One-shot effect: starts the proactive-refresh timer and the focus
 * listener. Mount once at the App level — mounting per-component
 * would multiply the timer count.
 */
export function useAuthBootstrap(): void {
  const accessToken = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    if (!accessToken) return;

    // 10-min interval, the §7.5 cadence. setInterval rather than
    // setTimeout-loop because the cadence is fixed and clearInterval
    // is the precise teardown.
    const TEN_MINUTES = 10 * 60 * 1000;
    const intervalId = window.setInterval(() => {
      void proactiveRefresh();
    }, TEN_MINUTES);

    const onFocus = () => {
      void proactiveRefresh();
    };
    window.addEventListener("focus", onFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, [accessToken]);
}
